import { AxiosRequestConfig, AxiosResponse } from "axios";
import { FileWithPath } from "react-dropzone";

import { ChatInputFormType } from "@/components/features/Conversations/components/ConversationMessageChat/components/ConversationChatInput/ConversationChatInput";

import { getSpApi, pagedRequestConfig, requestConfig } from "./apiRoot";
import { UrlManager } from "./UrlManager";

export const getRawConversations = async (
    filter: MyBrokerApi.SearchFilter = "all",
    pageSize = 1000,
): Promise<MyBrokerApi.ConversationPayload> => {
    const axiosRequestConfig = pagedRequestConfig<Paths.ApiConversation.Get.QueryParameters>({
        params: { filter, pageSize },
    });

    const { data } = await getSpApi().get<MyBrokerApi.ConversationPayload>(
        UrlManager.conversations(),
        axiosRequestConfig,
    );
    return data;
};

export const getConversations = async (
    filter: MyBrokerApi.SearchFilter = "all",
): Promise<MyBrokerApi.Conversation[]> => {
    const data = (await getRawConversations(filter)).result;
    return data;
};

export const getConversationsCount = async (filter: MyBrokerApi.SearchFilter = "all"): Promise<number> => {
    return (await getRawConversations(filter)).total;
};

export const getConversation = async (conversationId: string): Promise<MyBrokerApi.Conversation> => {
    const { data } = await getSpApi().get<MyBrokerApi.Conversation>(UrlManager.conversation(conversationId));
    return data;
};

export const getContextualConversation = async (
    objectType: "party" | "contract" | "risk" | "damage",
    objectId: string,
): Promise<MyBrokerApi.Conversation> => {
    const { data } = await getSpApi().get<MyBrokerApi.Conversation>(
        UrlManager.contextualConversations(objectType, objectId),
    );
    return data;
};

const getMultipartRequestConfig = (): AxiosRequestConfig => {
    const optionalRequestConfig: AxiosRequestConfig = {
        headers: {
            "Content-Type": "multipart/form-data",
        },
    };
    return requestConfig(optionalRequestConfig);
};

const getAttachmentsArray = (files: FileWithPath[]): MyBrokerApi.ConversationAttachement[] => {
    const attachments: MyBrokerApi.ConversationAttachement[] = [];
    if (files != null && files.length > 0) {
        for (const file of files) {
            attachments.push({
                size: file.size,
                description: file.name,
                type: file.type,
                name: file.name,
                lastModified: file.lastModified,
            });
        }
    }
    return attachments;
};

export const postMessage = async (
    conversationId: string,
    input: ChatInputFormType,
): Promise<AxiosResponse<MyBrokerApi.ConversationMessage>> => {
    const data = new FormData();
    const attachments = getAttachmentsArray(input.files);
    data.append(
        "jsonMessage",
        JSON.stringify({
            type: "text",
            body: input.content,
            attachments,
            sender: input.sender,
        }),
    );

    if (input.files != null && input.files.length > 0) {
        for (const file of input.files) {
            data.append("files", file);
        }
    }

    return getSpApi().post<any, AxiosResponse<MyBrokerApi.ConversationMessage>>(
        UrlManager.conversationPostMessage(conversationId),
        data,
    );
};

export interface ConversationContext {
    topic: string;
    id: string;
    type: string;
}

export const createConversation = async (
    policyholderId: number | null,
    input: ChatInputFormType,
    context?: ConversationContext,
): Promise<AxiosResponse<MyBrokerApi.Conversation>> => {
    const data = new FormData();
    const attachments = getAttachmentsArray(input.files);
    const payload =
        context != null
            ? {
                  topic: context.topic,
                  context: { id: context.id, type: context.type },
                  messages: [{ type: "text", body: input.content, attachments, sender: input.sender }],
              }
            : {
                  topic: input.title,
                  messages: [{ type: "text", body: input.content, attachments, sender: input.sender }],
              };
    data.append("jsonConversation", JSON.stringify(payload));

    if (input.files != null && input.files.length > 0) {
        for (const file of input.files) {
            data.append("files", file);
        }
    }

    const axiosRequestConfig = getMultipartRequestConfig();

    return getSpApi().post<any, AxiosResponse<MyBrokerApi.Conversation>>(
        UrlManager.createConversation(policyholderId),
        data,
        axiosRequestConfig,
    );
};
